import {
  Box,
  Dialog,
  Icon,
  Image,
  TransparentButton,
} from '@redriverpress/system'
import { motion } from 'framer-motion'
import PropTypes from 'prop-types'
import { Fragment, useRef, useState } from 'react'
import { use100vh } from 'react-div-100vh'
import styled from 'styled-components'

const MotionImage = motion(Image)

const StyledDialogContent = styled(Dialog.StyledContent)`
  background: transparent;
  width: 100%;
  max-width: 100% !important;
  height: 100%;
  max-height: 100% !important;
  overflow-y: unset;
  bottom: 0;
  margin: 0;
`

// Renders an image in a TransparentButton.
// When the button is clicked, opens the image in a full-screen modal,
// with the image taking up max 95vw and 95vh.
// Zoom in/out and close controls are rendered in the top right corner.
// Clicking the Zoom In button activates the `drag` prop on the zoomed-in image,
// allowing to be dragged (to pan around the zoomed image).
// The zoomed-in image is constrained to its parent's dimensions,
// so that it can't be dragged beyond this area.
export default function LightboxImage({ alt, src, openPortal, ...props }) {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)
  const [isZoomed, setIsZoomed] = useState(false)

  // The ref for the element that the image should not be able to be dragged beyond
  const constraintsRef = useRef(null)

  // Ref for the image in the lightbox; used to obtain the image's natural width and height.
  const imageRef = useRef(null)

  // Get the available viewport height (using just '100vh' doesn't account for
  // Safari's bottom UI bar on iOS)
  const viewportHeight = use100vh()

  const PortalComponent = openPortal ? Dialog.Portal : Fragment

  return (
    <>
      <TransparentButton
        aria-label='Open this image in a lightbox'
        onClick={() => setIsLightboxOpen(true)}
        p={0}
        lineHeight='normal'
        style={{
          cursor: 'zoom-in',
        }}
      >
        <Image src={src} alt={alt} mb={0} borderRadius='rounded' {...props} />
      </TransparentButton>
      <Dialog.Root
        open={isLightboxOpen}
        onOpenChange={open => setIsLightboxOpen(open)}
      >
        <PortalComponent>
          <Dialog.Overlay />
          <StyledDialogContent
            onPointerDownOutside={e => {
              e.preventDefault()
              setIsLightboxOpen(false)
            }}
          >
            <Box
              display='flex'
              alignItems='center'
              justifyContent='center'
              ref={constraintsRef}
              width='100%'
              height={viewportHeight}
            >
              <Box
                position='absolute'
                top={0}
                right={0}
                bg='rgba(0,0,0,1)'
                zIndex={10}
              >
                <TransparentButton
                  aria-label='Zoom image in'
                  p={3}
                  onClick={() => setIsZoomed(true)}
                  disabled={isZoomed}
                >
                  <Icon icon='search-plus' color='white' fontSize={3} />
                </TransparentButton>

                <TransparentButton
                  aria-label='Zoom image out'
                  p={3}
                  onClick={() => setIsZoomed(false)}
                  disabled={!isZoomed}
                >
                  <Icon icon='search-minus' color='white' fontSize={3} />
                </TransparentButton>

                <TransparentButton
                  aria-label='Close lightbox'
                  p={3}
                  onClick={() => {
                    setIsLightboxOpen(false)
                    setIsZoomed(false)
                  }}
                >
                  <Icon icon='times' color='white' fontSize={3} />
                </TransparentButton>
              </Box>

              <MotionImage
                src={src}
                alt={alt}
                ref={imageRef}
                zIndex={12}
                drag={isZoomed}
                dragConstraints={constraintsRef}
                dragElastic={0.125}
                dragMomentum={false}
                isZoomed={isZoomed}
                flex={0}
                maxWidth={isZoomed ? 'none' : '95vw'}
                maxHeight={isZoomed ? 'none' : '95vh'}
                animate={{
                  width: isZoomed ? imageRef.current.naturalWidth : '100%',
                  height: isZoomed ? imageRef.current.naturalHeight : 'auto',
                }}
                transition={{
                  stiffness: 300,
                  damping: 30,
                }}
                initial={false}
                style={{
                  cursor: isZoomed ? 'move' : 'auto',
                  x: !isZoomed && 0, // Ensure image position is reset…
                  y: !isZoomed && 0, // …when zooming out (if the image has been dragged)
                }}
              />
            </Box>
          </StyledDialogContent>
        </PortalComponent>
      </Dialog.Root>
    </>
  )
}

LightboxImage.defaultProps = {
  openPortal: true,
}

LightboxImage.propTypes = {
  alt: PropTypes.string.isRequired,
  src: PropTypes.string.isRequired,
  openPortal: PropTypes.bool,
}
